// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

export type LogLevel = 'info' | 'good' | 'warn' | 'error';

const MAX_EVENTS = 600;

interface Entry {
  readonly at: Date;
  readonly level: LogLevel;
  readonly message: string;
}

/**
 * The event drawer.
 *
 * IT RECORDS VARIABLE NAMES, ENCODINGS, PATHS AND COUNTS. IT NEVER RECORDS A VALUE.
 *
 * That is channel 11 of the leak-channel table, and the reason it is a hard rule rather than a
 * convention: the log is copyable, screenshot-able and the first thing anyone pastes into a bug
 * report, so a single line reading `matched sk-live-…` would carry the secret straight into a
 * public issue tracker — via the one part of the interface that exists to build trust.
 */
export class EventLog {
  private readonly entries: Entry[] = [];

  constructor(private readonly list: HTMLOListElement) {}

  emit(level: LogLevel, message: string): void {
    const entry = { at: new Date(), level, message };
    this.entries.push(entry);
    if (this.entries.length > MAX_EVENTS) this.entries.shift();

    const li = document.createElement('li');
    const t = document.createElement('span');
    t.className = 't';
    t.textContent = `[${entry.at.toTimeString().slice(0, 8)}] `;
    const m = document.createElement('span');
    m.className = `lv-${level}`;
    // textContent, never innerHTML: log lines quote file names, which are user-controlled.
    m.textContent = message;
    li.append(t, m);
    this.list.appendChild(li);
    while (this.list.children.length > MAX_EVENTS) this.list.firstElementChild?.remove();
    this.list.scrollTop = this.list.scrollHeight;
  }

  asText(): string {
    return this.entries.map((e) => `[${e.at.toISOString()}] ${e.level.toUpperCase()} ${e.message}`).join('\n');
  }
}
